import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TranslocoService } from "@ngneat/transloco";
import { ToastrService } from "ngx-toastr";
import { Compbaser } from "../compbaser";
import { ISchedule, IScheduleEvent } from "../../interfaces";
import { IExpandedOccurrence, ISchedulerReportData, SchedulerReportService } from "../../services/scheduler-report.service";
import * as Highcharts from "highcharts";
import moment from "moment";

export interface DialogSchedulerReportData {
    schedule: ISchedule;
    events: IScheduleEvent[];
    daysToExpand?: number;
}

@Component({
    selector: "app-dialog-scheduler-report",
    templateUrl: "./dialog-scheduler-report.component.html",
    styleUrls: ["./dialog-scheduler-report.component.scss"],
})
export class DialogSchedulerReportComponent extends Compbaser implements OnInit {
    reportData: ISchedulerReportData;
    loading = true;
    daysToExpand = 90;
    selectedTimeRange: "7" | "30" | "90" | "custom" = "90";
    customDays = 90;
    actualStartDate: moment.Moment | undefined;
    actualEndDate: moment.Moment | undefined;

    // Highcharts integration
    Highcharts: typeof Highcharts = Highcharts;
    chartsReady = false;
    recurrenceChart: Highcharts.Options;
    contentUsageChart: Highcharts.Options;
    dailyDistributionChart: Highcharts.Options;
    hourlyDistributionChart: Highcharts.Options;
    weeklyPatternChart: Highcharts.Options;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: DialogSchedulerReportData,
        private dialogRef: MatDialogRef<DialogSchedulerReportComponent>,
        public override dialog: MatDialog,
        public schedulerReportService: SchedulerReportService,
        private translocoService: TranslocoService,
        private toastr: ToastrService,
    ) {
        super(dialog);
        if (data.daysToExpand) {
            this.daysToExpand = data.daysToExpand;
        }
    }

    ngOnInit(): void {
        this.generateReport();
    }

    generateReport(): void {
        this.loading = true;
        this.chartsReady = false;
        setTimeout(() => {
            // Filter events to only include those for the selected schedule
            // Handle type mismatches between string and number IDs
            const filteredEvents = this.data.events.filter(event =>
                event.schedule_id === this.data.schedule.schedule_id ||
                String(event.schedule_id) === String(this.data.schedule.schedule_id),
            );

            // Calculate custom date range based on selected time range
            let customStartDate: moment.Moment | undefined;
            let customEndDate: moment.Moment | undefined;

            if (this.selectedTimeRange === "7") {
                // For "1 week", show current calendar week from Sunday to Saturday (7 days)
                customStartDate = moment().startOf("week"); // Sunday (week starts Sunday in moment.js)
                customEndDate = moment().endOf("week"); // Saturday (end of week)
            } else if (this.selectedTimeRange === "30") {
                // For "1 month", show current calendar month from 1st to last day
                customStartDate = moment().startOf("month"); // 1st day of current month
                customEndDate = moment().endOf("month"); // Last day of current month
            } else if (this.selectedTimeRange === "90") {
                // For "90 days / 1 quarter", show current quarter from first to last day
                customStartDate = moment().startOf("quarter"); // 1st day of current quarter
                customEndDate = moment().endOf("quarter"); // Last day of current quarter
            }

            // Store the actual dates being used for the report
            this.actualStartDate = customStartDate || moment().startOf("day");
            this.actualEndDate = customEndDate || moment().add(this.daysToExpand, "days").endOf("day");

            this.reportData = this.schedulerReportService.generateReportData(
                this.data.schedule,
                filteredEvents,
                this.daysToExpand,
                customStartDate,
                customEndDate,
            );
            this.loading = false;

            // Generate charts after report data is ready
            setTimeout(() => {
                this.generateCharts();
            }, 50);
        }, 100);
    }

    close(): void {
        this.dialogRef.close();
    }

    printReport(): void {
        window.print();
    }

    copyToClipboard(): void {
        const reportText = this.generateTextReport();
        navigator.clipboard.writeText(reportText).then(() => {
            this.toastr.success(this.translocoService.translate("toast.copiedToClipboard", {}, this.translocoService.getActiveLang()));
        });
    }

    private generateTextReport(): string {
        if (!this.reportData) return "";

        const lines: string[] = [];
        const { schedule, statistics, events } = this.reportData;

        lines.push("=".repeat(80));
        lines.push(`SCHEDULE REPORT: ${schedule.schedule_name}`);
        lines.push("=".repeat(80));
        lines.push("");

        if (schedule.description) {
            lines.push(`Description: ${schedule.description}`);
            lines.push("");
        }

        lines.push("--- SUMMARY STATISTICS ---");
        lines.push(`Total Events: ${statistics.totalEvents}`);
        lines.push(`Active Events: ${statistics.activeEvents}`);
        lines.push(`Inactive Events: ${statistics.inactiveEvents}`);
        lines.push(`Scenes: ${statistics.sceneCount}`);
        lines.push(`Resources: ${statistics.resourceCount}`);
        lines.push(`Total Daily Duration: ${this.schedulerReportService.formatDuration(statistics.totalDailyDuration)}`);
        lines.push(`Average Event Duration: ${this.schedulerReportService.formatDuration(statistics.averageEventDuration)}`);
        lines.push(`Earliest Time: ${statistics.earliestTime}`);
        lines.push(`Latest Time: ${statistics.latestTime}`);
        lines.push(`Utilization: ${statistics.utilizationPercentage}%`);
        lines.push("");

        lines.push("--- SCHEDULED CONTENT ---");
        const visibleOccurrences = this.getVisibleOccurrences();
        visibleOccurrences.forEach((occ, index) => {
            const duration = occ.effectiveDuration || this.getEventDuration(occ.event);
            lines.push(`${index + 1}. ${occ.date} (${occ.dayOfWeek}) - ${occ.time} - ${occ.event.event_name} - ${this.schedulerReportService.formatDuration(duration)}`);
        });
        lines.push("");

        return lines.join("\n");
    }

    getEventType(event: IScheduleEvent): string {
        if (event.event_value) {
            try {
                const value = JSON.parse(event.event_value);
                return value.type || "unknown";
            } catch (e) {
                return "unknown";
            }
        }
        return "unknown";
    }

    getEventName(event: IScheduleEvent): string {
        if (event.event_value) {
            try {
                const value = JSON.parse(event.event_value);
                return value.name || event.event_name;
            } catch (e) {
                return event.event_name;
            }
        }
        return event.event_name;
    }

    getRecurrenceDescription(event: IScheduleEvent): string {
        const type = this.schedulerReportService.getRecurrenceTypeName(event.repeat_type);

        if (event.repeat_type === 3) { // WEEKLY
            const days = this.schedulerReportService.getDayNames(event.weekly_days || 0);
            return `${type} (${days.join(", ")})`;
        }

        if (event.repeat_type === 2) { // MONTHLY
            return `${type} (day ${event.monthly_offset || 1})`;
        }

        if (event.repeat_type === 1) { // YEARLY
            const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
            return `${type} (${months[event.yearly_offset || 0]})`;
        }

        if (event.repeat_type === 4) { // HOURLY
            return `${type} (every ${event.hourly_offset || 1}h, ${event.repeat_hourly || 1}x)`;
        }

        return type;
    }

    /**
     * Format hour with leading zero
     */
    formatHour(hour: number): string {
        return String(hour).padStart(2, "0");
    }

    /**
     * Format date to show only date part (YYYY-MM-DD)
     */
    formatDate(dateValue: string | Date): string {
        if (!dateValue) return "";

        // Use moment to parse and format the date
        const date = moment(dateValue);
        if (!date.isValid()) return String(dateValue);

        return date.format("YYYY-MM-DD");
    }

    /**
     * Get day of week from date string
     */
    getDayOfWeek(dateValue: string): string {
        const date = moment(dateValue);
        if (!date.isValid()) return "";
        return date.format("dddd");
    }

    /**
     * Get event duration in minutes (convert from seconds if needed)
     */
    getEventDuration(event: IScheduleEvent): number {
        const durationInSeconds = typeof event.duration === "string" ? parseInt(event.duration) : event.duration;
        return Math.round((durationInSeconds || 0) / 60); // Convert seconds to minutes
    }

    /**
     * Get event end time based on start time + duration
     */
    getEventEndTime(event: IScheduleEvent): string {
        const timeInSeconds = typeof event.time === "string" ? parseInt(event.time) : event.time;
        const durationInSeconds = typeof event.duration === "string" ? parseInt(event.duration) : event.duration;

        // Convert to hours
        const startHours = timeInSeconds / 3600;
        const durationHours = (durationInSeconds || 0) / 3600;
        let endHours = startHours + durationHours;

        // Cap at 24 hours (end of day)
        if (endHours > 24) {
            endHours = 24;
        }

        return this.schedulerReportService.formatTime(endHours);
    }

    /**
     * Get visible occurrences (filter out those with effectiveDuration = 0)
     * Events with effectiveDuration = 0 have been completely overridden by higher priority events
     */
    getVisibleOccurrences(): IExpandedOccurrence[] {
        if (!this.reportData || !this.reportData.expandedOccurrences) {
            return [];
        }

        return this.reportData.expandedOccurrences.filter(occ => {
            const effectiveDuration = occ.effectiveDuration ?? this.getEventDuration(occ.event);
            return effectiveDuration > 0;
        });
    }

    /**
     * Get total effective duration across all occurrences
     */
    getTotalEffectiveDuration(): number {
        if (!this.reportData || !this.reportData.expandedOccurrences) {
            return 0;
        }

        return this.reportData.expandedOccurrences.reduce((sum, occ) => {
            const duration = occ.effectiveDuration || this.getEventDuration(occ.event);
            return sum + duration;
        }, 0);
    }

    /**
     * Get formatted forecast period string
     */
    getForecastPeriod(): string {
        if (!this.actualStartDate || !this.actualEndDate) {
            return `Next ${this.daysToExpand} days`;
        }

        const startFormatted = this.actualStartDate.format("MMM D, YYYY");
        const endFormatted = this.actualEndDate.format("MMM D, YYYY");

        if (this.selectedTimeRange === "7") {
            return `${startFormatted} - ${endFormatted} (Current Week)`;
        } else if (this.selectedTimeRange === "30") {
            const monthName = this.actualStartDate.format("MMMM YYYY");
            return `${startFormatted} - ${endFormatted} (${monthName})`;
        } else if (this.selectedTimeRange === "90") {
            const quarter = this.actualStartDate.quarter();
            const year = this.actualStartDate.year();
            return `${startFormatted} - ${endFormatted} (Q${quarter} ${year})`;
        }

        return `Next ${this.daysToExpand} days`;
    }

    /**
     * Get period description for headers (shorter format)
     */
    getPeriodDescription(): string {
        if (!this.actualStartDate || !this.actualEndDate) {
            return `NEXT ${this.daysToExpand} DAYS`;
        }

        if (this.selectedTimeRange === "7") {
            return "CURRENT WEEK";
        } else if (this.selectedTimeRange === "30") {
            const monthName = this.actualStartDate.format("MMMM YYYY").toUpperCase();
            return monthName;
        } else if (this.selectedTimeRange === "90") {
            const quarter = this.actualStartDate.quarter();
            const year = this.actualStartDate.year();
            return `Q${quarter} ${year}`;
        }

        return `NEXT ${this.daysToExpand} DAYS`;
    }

    /**
     * Get current date and time for report header
     */
    getCurrentDateTime(): string {
        const now = new Date();
        const date = now.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
        const time = now.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
        });
        return `${date} at ${time}`;
    }

    onTimeRangeChange(range: "7" | "30" | "90" | "custom"): void {
        this.selectedTimeRange = range;
        if (range !== "custom") {
            this.daysToExpand = parseInt(range);
            this.customDays = this.daysToExpand;
            this.generateReport();
        }
    }

    onCustomDaysChange(days: number): void {
        this.customDays = days;
        this.daysToExpand = days;
        this.generateReport();
    }

    /**
     * Generate all charts
     */
    private generateCharts(): void {
        if (!this.reportData) return;

        this.generateRecurrenceChart();
        this.generateContentUsageChart();
        this.generateDailyDistributionChart();
        this.generateHourlyDistributionChart();
        this.generateWeeklyPatternChart();

        this.chartsReady = true;
    }

    /**
     * Chart 1: Recurrence Pattern Pie Chart
     */
    private generateRecurrenceChart(): void {
        let data = [
            { name: "Once", y: this.reportData.recurrenceBreakdown.once.length, color: "#2196F3" },
            { name: "Daily", y: this.reportData.recurrenceBreakdown.daily.length, color: "#4CAF50" },
            { name: "Weekly", y: this.reportData.recurrenceBreakdown.weekly.length, color: "#FF9800" },
            { name: "Monthly", y: this.reportData.recurrenceBreakdown.monthly.length, color: "#9C27B0" },
            { name: "Yearly", y: this.reportData.recurrenceBreakdown.yearly.length, color: "#F44336" },
            { name: "Hourly", y: this.reportData.recurrenceBreakdown.hourly.length, color: "#00BCD4" },
        ].filter(item => item.y > 0); // Only show categories with data

        // Handle empty data case
        if (data.length === 0) {
            data = [{ name: "No Events", y: 1, color: "#CCCCCC" }];
        }

        this.recurrenceChart = {
            chart: { type: "pie" },
            title: { text: "event recurrence patterns", style: { fontSize: "14px", fontWeight: "bold" } },
            credits: { enabled: false },
            tooltip: {
                headerFormat: "",
                pointFormat: "<b>{point.name}</b>: {point.y} events ({point.percentage:.1f}%)",
                style: { color: "var(--text-color1)" },
            },
            plotOptions: {
                pie: {
                    allowPointSelect: true,
                    cursor: "pointer",
                    dataLabels: {
                        enabled: true,
                        format: "<b>{point.name}</b>: {point.percentage:.1f}%",
                        style: { color: "var(--text-color1)" },
                    },
                },
            },
            series: [{ type: "pie", name: "Events", data }],
        };
    }

    /**
     * Chart 2: Content Usage Bar Chart (Top 10)
     */
    private generateContentUsageChart(): void {
        const sorted = [...this.reportData.resourceInventory]
            .sort((a, b) => b.usageCount - a.usageCount)
            .slice(0, 10);

        // Handle empty data case
        if (sorted.length === 0) {
            sorted.push({ id: "none", name: "No Content", type: "resource", usageCount: 0 });
        }

        const categories = sorted.map(item => item.name);
        const data = sorted.map(item => ({
            y: item.usageCount,
            color: item.type === "scene" ? "#2196F3" : "#4CAF50",
        }));

        this.contentUsageChart = {
            chart: { type: "bar" },
            title: { text: "top 10 content usage", style: { fontSize: "14px", fontWeight: "bold" } },
            credits: { enabled: false },
            xAxis: {
                categories,
                title: { text: null },
                labels: { style: { color: "var(--text-color1)" } },
            },
            yAxis: {
                min: 0,
                title: { text: "usage count", style: { color: "var(--text-color1)" } },
                labels: { style: { color: "var(--text-color1)" } },
            },
            tooltip: {
                valueSuffix: " times",
                style: { color: "var(--text-color1)" },
            },
            plotOptions: {
                bar: { dataLabels: { enabled: true } },
            },
            legend: { enabled: false },
            series: [{ type: "bar", name: "Usage", data }],
        };
    }

    /**
     * Chart 3: Daily Event Distribution
     */
    private generateDailyDistributionChart(): void {
        const dailyCount = new Map<string, number>();
        const visibleOccurrences = this.getVisibleOccurrences();
        visibleOccurrences.forEach(occ => {
            dailyCount.set(occ.date, (dailyCount.get(occ.date) || 0) + 1);
        });

        let categories = Array.from(dailyCount.keys()).slice(0, 30); // Show first 30 days

        // Handle empty data case
        if (categories.length === 0) {
            const today = moment().format("YYYY-MM-DD");
            categories = [today];
            dailyCount.set(today, 0);
        }

        const data = categories.map(date => dailyCount.get(date) || 0);

        this.dailyDistributionChart = {
            chart: { type: "column" },
            title: { text: "events per day (next 30 days)", style: { fontSize: "14px", fontWeight: "bold" } },
            credits: { enabled: false },
            xAxis: {
                categories: categories.map(d => moment(d).format("MMM DD")),
                labels: { style: { color: "var(--text-color1)" }, rotation: -45 },
            },
            yAxis: {
                min: 0,
                title: { text: "number of events", style: { color: "var(--text-color1)" } },
                labels: { style: { color: "var(--text-color1)" } },
            },
            tooltip: {
                headerFormat: "<b>{point.key}</b><br/>",
                pointFormat: "{series.name}: {point.y} events",
                style: { color: "var(--text-color1)" },
            },
            plotOptions: { column: { color: "#2196F3" } },
            legend: { enabled: false },
            series: [{ type: "column", name: "Events", data }],
        };
    }

    /**
     * Chart 4: Hourly Distribution
     */
    private generateHourlyDistributionChart(): void {
        const hourlyCount = new Array(24).fill(0);

        // Only process visible occurrences
        const visibleOccurrences = this.getVisibleOccurrences();
        if (visibleOccurrences && visibleOccurrences.length > 0) {
            visibleOccurrences.forEach(occ => {
                const hour = parseInt(occ.time.split(":")[0]);
                if (!isNaN(hour) && hour >= 0 && hour < 24) {
                    hourlyCount[hour]++;
                }
            });
        }

        const categories = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);

        this.hourlyDistributionChart = {
            chart: { type: "column" },
            title: { text: "event distribution by hour", style: { fontSize: "14px", fontWeight: "bold" } },
            credits: { enabled: false },
            xAxis: {
                categories,
                title: { text: "hour of day", style: { color: "var(--text-color1)" } },
                labels: { style: { color: "var(--text-color1)" } },
            },
            yAxis: {
                min: 0,
                title: { text: "total occurrences", style: { color: "var(--text-color1)" } },
                labels: { style: { color: "var(--text-color1)" } },
            },
            tooltip: {
                headerFormat: "<b>{point.key}</b><br/>",
                pointFormat: "{point.y} events",
                style: { color: "var(--text-color1)" },
            },
            plotOptions: { column: { color: "#4CAF50" } },
            legend: { enabled: false },
            series: [{ type: "column", name: "Events", data: hourlyCount }],
        };
    }

    /**
     * Chart 5: Weekly Pattern
     */
    private generateWeeklyPatternChart(): void {
        const weeklyCount = { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 };

        // Only process visible occurrences
        const visibleOccurrences = this.getVisibleOccurrences();
        if (visibleOccurrences && visibleOccurrences.length > 0) {
            visibleOccurrences.forEach(occ => {
                const day = moment(occ.date).format("ddd");
                if (weeklyCount.hasOwnProperty(day)) {
                    weeklyCount[day]++;
                }
            });
        }

        const categories = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const data = categories.map(day => weeklyCount[day] || 0);

        this.weeklyPatternChart = {
            chart: { type: "column" },
            title: { text: "weekly distribution pattern", style: { fontSize: "14px", fontWeight: "bold" } },
            credits: { enabled: false },
            xAxis: {
                categories,
                title: { text: "day of week", style: { color: "var(--text-color1)" } },
                labels: { style: { color: "var(--text-color1)" } },
            },
            yAxis: {
                min: 0,
                title: { text: "total occurrences", style: { color: "var(--text-color1)" } },
                labels: { style: { color: "var(--text-color1)" } },
            },
            tooltip: {
                headerFormat: "<b>{point.key}</b><br/>",
                pointFormat: "{point.y} events",
                style: { color: "var(--text-color1)" },
            },
            plotOptions: { column: { color: "#FF9800" } },
            legend: { enabled: false },
            series: [{ type: "column", name: "Events", data }],
        };
    }

    chartCallback = (chart) => {
        setTimeout(() => {
            if (!chart || !chart.callback || chart.callback.length === 0) return;
            chart.reflow();
        }, 1);
    };
}
